import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import RouteFlashSnackbar from '@/shared/components/RouteFlashSnackbar/RouteFlashSnackbar';
import UpdateStatusDialog from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { useQmsCategoryList } from './useQmsCategoryList';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import ErrorPage403 from '@/app/routes/errorPage403';
import type { FormattedQmsCategory } from './qmsCategoryListValidation';
import { QMS_CATEGORY_LABELS } from '../../constants/labels';
import { QMS_CATEGORY_ERROR_MESSAGES } from '../../errors/errorMessages';
import { useUpdateQmsCategoryStatus } from '../../hooks/useUpdateQmsCategoryStatus';
import { getQmsCategoryStatusDialogConfig } from './qmsCategoryStatusDialogConfig';
import qmsListingAnimation from '@/assets/lottie/qms_listing_drawer_animation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';

const CREATE_PATH = '/Kategorie/Dodaj-kategorię';
const MIN_SPACE_BELOW_FOR_MENU = 70;

const QmsCategoryList: React.FC = () => {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const menuPortalRef = useRef<HTMLDivElement | null>(null);
  const prevOpenMenuRowIdRef = useRef<string | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    bottom: number;
    right: number;
  } | null>(null);
  const [openMenuRow, setOpenMenuRow] = useState<FormattedQmsCategory | null>(
    null
  );

  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();

  const {
    categories,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    statusOptions,
    setStatusOptions,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    fetchError,
    setFetchError,
    accessError,
    limitError,
    handleLimitChange,
    refetch,
  } = useQmsCategoryList();

  const {
    selectedCategory,
    isDialogOpen,
    isSubmitting: isStatusSubmitting,
    handleOpenDialog,
    handleDialogClose,
    handleConfirm,
    showSuccessSnackbar,
    successMessage,
    setShowSuccessSnackbar,
    showErrorSnackbar,
    errorMessage: statusErrorMessage,
    setShowErrorSnackbar,
  } = useUpdateQmsCategoryStatus({ onSuccess: refetch });

  const closeMenu = useCallback(() => {
    setOpenMenuRowId(null);
    setOpenMenuRow(null);
    setMenuPosition(null);
  }, []);

  // Close actions menu when clicking outside
  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inAnchor = menuAnchorRef.current?.contains(target) ?? false;
      const inPortal = menuPortalRef.current?.contains(target) ?? false;
      if (!inAnchor && !inPortal) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuRowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on scroll (portal is fixed-positioned so doesn't follow the button)
  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    window.addEventListener('scroll', closeMenu, true);
    return () => window.removeEventListener('scroll', closeMenu, true);
  }, [openMenuRowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus first menu item on open; restore trigger focus on close
  useEffect(() => {
    if (openMenuRowId !== null) {
      prevOpenMenuRowIdRef.current = openMenuRowId;
      requestAnimationFrame(() => {
        const firstMenuitem =
          menuPortalRef.current?.querySelector<HTMLElement>(
            '[role="menuitem"]'
          );
        firstMenuitem?.focus();
      });
    } else {
      const hadOpen = prevOpenMenuRowIdRef.current !== null;
      prevOpenMenuRowIdRef.current = null;
      if (hadOpen && menuTriggerRef.current) {
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    }
  }, [openMenuRowId]);

  const columns: Column<FormattedQmsCategory>[] = useMemo(
    () => [
      {
        key: 'name',
        header: QMS_CATEGORY_LABELS.COLUMN_NAME,
        minWidth: '130px',
        render: (row) => (
          <div
            className="flex-1 min-w-0"
            onMouseDown={(e) => e.preventDefault()}
          >
            <Tooltip content={row.name}>
              <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.name}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'description',
        header: QMS_CATEGORY_LABELS.COLUMN_DESCRIPTION,
        minWidth: '160px',
        render: (row) => (
          <div
            className="flex-1 min-w-0"
            onMouseDown={(e) => e.preventDefault()}
          >
            <Tooltip content={row.description || ''} useContentWidth>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.description || '—'}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'tags',
        header: QMS_CATEGORY_LABELS.COLUMN_TAGS,
        minWidth: '215px',
        render: (row) => {
          const { tags } = row;
          if (!tags || tags.length === 0) {
            return (
              <div className="flex w-[100px] justify-start px-[10px]">
                <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
                  —
                </span>
              </div>
            );
          }
          const [first, ...rest] = tags;
          if (!first) {
            return (
              <div className="flex w-[100px] justify-start px-[10px]">
                <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
                  —
                </span>
              </div>
            );
          }
          const extra = rest.length;
          return (
            <div
              className="flex items-center gap-1.5 min-w-0"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Tooltip
                content={first.name}
                inlineTrigger
                align="center"
                useContentWidth
              >
                <span className="inline-flex items-center h-8 px-2 rounded-lg bg-information-90 text-information-30 text-sm font-medium leading-5 tracking-[0.1px] shrink-0 max-w-[100px]">
                  <span className="truncate">{first.name}</span>
                </span>
              </Tooltip>
              {extra > 0 && (
                <Tooltip
                  content={rest.map((t) => t.name).join(', ')}
                  inlineTrigger
                  align="center"
                  useContentWidth
                >
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-information-30 text-xs font-medium leading-[18px] tracking-[0.1px] shrink-0 cursor-pointer">
                    +{extra} {QMS_CATEGORY_LABELS.MORE_TAGS_SUFFIX}
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        key: 'createdAt',
        header: QMS_CATEGORY_LABELS.COLUMN_CREATED_AT,
        minWidth: '140px',
        render: (row) => (
          <div
            className="flex-1 min-w-0"
            onMouseDown={(e) => e.preventDefault()}
          >
            <Tooltip content={row.formattedCreatedAt} useContentWidth>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.formattedCreatedAt}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'status',
        header: QMS_CATEGORY_LABELS.COLUMN_STATUS,
        minWidth: '160px',
        noTruncate: true,
        render: (row) => {
          const canToggle = privileges.qmsDocCategoryActivateDeactivate;
          const toggle = (
            <div
              className={`inline-flex ${canToggle ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
              onClick={() => {
                if (!canToggle) {
                  return;
                }
                handleOpenDialog(row);
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <div style={{ pointerEvents: 'none' }}>
                <ToggleButton
                  checked={row.status === 'active'}
                  onChange={() => {}}
                  label={
                    row.status === 'active'
                      ? QMS_CATEGORY_LABELS.STATUS_ACTIVE
                      : QMS_CATEGORY_LABELS.STATUS_INACTIVE
                  }
                  size="Small"
                />
              </div>
            </div>
          );
          if (!canToggle) {
            return (
              <Tooltip
                content={QMS_CATEGORY_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                side="left"
                forceShow
                triggerWrapContent
              >
                {toggle}
              </Tooltip>
            );
          }
          return toggle;
        },
      },
      {
        key: 'id',
        header: QMS_CATEGORY_LABELS.COLUMN_ACTIONS,
        width: '60px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    setMenuOpensUpward(spaceBelow < MIN_SPACE_BELOW_FOR_MENU);
                    setMenuPosition({
                      top: rect.bottom + 4,
                      bottom: window.innerHeight - rect.top + 4,
                      right: window.innerWidth - rect.right,
                    });
                    setOpenMenuRow(row);
                  } else {
                    setMenuPosition(null);
                    setOpenMenuRow(null);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Akcje dla kategorii ${row.name}`}
                data-testid={`action-menu-button-${index ?? 0}`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          );
        },
      },
    ],
    [openMenuRowId, handleOpenDialog, privileges]
  );

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = () => {
    setStatusOptions(appliedFilters.statusOptions);
    setIsFilterOpen(false);
  };

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    categories.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !search.trim() &&
    !appliedFilters.statusOptions.some((opt) => opt.checked);

  if (!isPrivilegesLoading && !privileges.qmsDocCategoryList) {
    return <ErrorPage403 />;
  }
  if (accessError) {
    return <ErrorPage403 />;
  }

  return (
    <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
      <RouteFlashSnackbar stateKey="navigationSuccessMessage" />

      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="text-[#181A1B] text-[22px] font-normal leading-7">
          {QMS_CATEGORY_LABELS.LIST_TITLE}
        </div>
        {!hasNoData &&
          (privileges.qmsDocCategoryCreate ? (
            <button
              type="button"
              onClick={() => navigate(CREATE_PATH)}
              data-testid="dodaj-kategorię-button"
              className="h-10 bg-[#2A4455] px-4 py-2.5 flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            >
              <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                {QMS_CATEGORY_LABELS.BUTTON_CREATE}
              </span>
            </button>
          ) : (
            <Tooltip
              content={QMS_CATEGORY_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
              side="left"
              forceShow
              triggerWrapContent
            >
              <button
                type="button"
                disabled
                data-testid="dodaj-kategorię-button"
                className="h-10 bg-[#B0BEC5] px-4 py-2.5 flex items-center justify-center rounded-lg cursor-not-allowed"
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  {QMS_CATEGORY_LABELS.BUTTON_CREATE}
                </span>
              </button>
            </Tooltip>
          ))}
      </div>

      {/* Search + Filter — hidden when truly no data */}
      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={QMS_CATEGORY_LABELS.SEARCH_PLACEHOLDER}
          />
          <div className={`dropdown ${isFilterOpen ? 'show' : ''}`}>
            <style>{`
              .dropdown { position: relative; }
              .dropdown.show .dropdown-content { display: block; }
            `}</style>
            <FilterBar
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              isOpen={isFilterOpen}
              filterCount={
                appliedFilters.statusOptions.filter((opt) => opt.checked).length
              }
            />
            <FilterPopup
              isOpen={isFilterOpen}
              onClose={handleCloseFilter}
              showDateRange={false}
              statusOptions={statusOptions}
              onStatusChange={setStatusOptions}
              onApply={handleApplyFiltersAndClose}
              onClear={handleClearFilters}
            />
          </div>
        </div>
      )}

      {/* Table / Empty / No-results */}
      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 flex flex-col overflow-x-auto overflow-y-auto"
      >
        {categories.length === 0 && !loading && !isSearchClearing ? (
          hasNoData ? (
            /* True empty state — no data at all */
            <div className="flex flex-col items-center justify-center w-full h-full">
              <div style={{ marginBottom: -60 }}>
                <LottieAnimation
                  animationData={qmsListingAnimation}
                  width={560}
                  aspectRatio="372 / 294"
                />
              </div>
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="flex flex-col items-center gap-1">
                  <p className="w-[548px] text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                    {QMS_CATEGORY_LABELS.EMPTY_STATE_TITLE}
                  </p>
                  <p className="w-[548px] text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                    {QMS_CATEGORY_LABELS.EMPTY_STATE_DESCRIPTION}
                  </p>
                </div>
                {privileges.qmsDocCategoryCreate ? (
                  <button
                    type="button"
                    onClick={() => navigate(CREATE_PATH)}
                    className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                  >
                    <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                      {QMS_CATEGORY_LABELS.BUTTON_CREATE}
                    </span>
                  </button>
                ) : (
                  <Tooltip
                    content={QMS_CATEGORY_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                    side="bottom"
                    forceShow
                    triggerWrapContent
                  >
                    <button
                      type="button"
                      disabled
                      className="h-10 bg-[#B0BEC5] px-4 py-2.5 rounded-lg cursor-not-allowed flex items-center justify-center"
                    >
                      <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                        {QMS_CATEGORY_LABELS.BUTTON_CREATE}
                      </span>
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          ) : (
            /* No results — search/filter returned nothing */
            <div className="flex flex-col items-center justify-center w-full h-full gap-3">
              <LottieAnimation
                animationData={noResultsAnimation}
                width="clamp(160px, 18vw, 360px)"
                aspectRatio="432 / 350"
              />
              <div className="w-[548px] max-w-full flex flex-col items-center justify-start gap-1">
                <div className="w-full text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                  {QMS_CATEGORY_LABELS.NO_RESULTS_TITLE}
                </div>
                <div className="w-full text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                  {search.trim() &&
                  appliedFilters.statusOptions.some((opt) => opt.checked)
                    ? QMS_CATEGORY_LABELS.NO_RESULTS_DESCRIPTION_BOTH
                    : search.trim()
                      ? QMS_CATEGORY_LABELS.NO_RESULTS_DESCRIPTION_SEARCH
                      : QMS_CATEGORY_LABELS.NO_RESULTS_DESCRIPTION_FILTER}
                </div>
              </div>
            </div>
          )
        ) : (
          <Table
            columns={columns}
            data={categories}
            loading={loading}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            getStickyCellZIndex={(_row, _idx, columnKey) =>
              columnKey === 'id' && openMenuRowId !== null ? 50 : 1
            }
          />
        )}
      </div>

      {/* Pagination */}
      {!hasNoData && totalCount > 10 && (
        <div className="shrink-0 px-4 -mx-4">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </div>
          )}
        </div>
      )}

      {/* Fetch error snackbar */}
      <Snackbar
        open={!!fetchError}
        message={fetchError}
        variant="error"
        onClose={() => setFetchError('')}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />

      {/* Status toggle dialog */}
      {selectedCategory && (
        <UpdateStatusDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleConfirm}
          isSubmitting={isStatusSubmitting}
          {...getQmsCategoryStatusDialogConfig(
            selectedCategory.name,
            selectedCategory.status === 'active'
          )}
        />
      )}

      {/* Status success snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        onClose={() => setShowSuccessSnackbar(false)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton={true}
      />

      {/* Status error snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={() => setShowErrorSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />

      {/* Actions menu portal — rendered at document.body to escape overflow clipping */}
      {openMenuRow &&
        menuPosition &&
        createPortal(
          <div
            ref={menuPortalRef}
            style={{
              position: 'fixed',
              zIndex: 9999,
              right: menuPosition.right,
              ...(menuOpensUpward
                ? { bottom: menuPosition.bottom }
                : { top: menuPosition.top }),
            }}
            role="presentation"
            onKeyDown={(e) => {
              if (e.key !== 'Tab') {
                return;
              }
              const items =
                menuPortalRef.current?.querySelectorAll<HTMLElement>(
                  '[role="menuitem"]'
                );
              const lastItem = items ? items[items.length - 1] : undefined;
              if (
                lastItem &&
                document.activeElement === lastItem &&
                !e.shiftKey
              ) {
                closeMenu();
              }
            }}
          >
            <Menu
              items={[
                {
                  id: 'VIEW',
                  label: QMS_CATEGORY_LABELS.ACTION_VIEW,
                  disabled: !privileges.qmsDocCategoryView,
                  disabledTooltip:
                    QMS_CATEGORY_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                  disabledTooltipSide: 'left',
                },
                {
                  id: 'EDIT',
                  label: QMS_CATEGORY_LABELS.ACTION_EDIT,
                  disabled: !privileges.qmsDocCategoryEdit,
                  disabledTooltip:
                    QMS_CATEGORY_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                  disabledTooltipSide: 'left',
                },
              ]}
              variant={MenuVariant.Light}
              size={MenuSize.Small}
              toolTip={false}
              onItemClick={(itemId) => {
                const { id } = openMenuRow;
                closeMenu();
                if (itemId === 'VIEW') {
                  navigate(`/Kategorie/Podgląd/${id}`);
                } else if (itemId === 'EDIT') {
                  navigate(`/Kategorie/Edycja/${id}`);
                }
              }}
              showLeadingIcons={false}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default QmsCategoryList;
